import { useState, useEffect, useMemo, useRef, useId } from "react";

import { useAtom } from "jotai";
import { authenticationAtom, jwtTokensAtom } from '../State';
import { useNavigate, useLocation } from "react-router-dom";
import { userAPI } from "../UserAPI";
import PasswordChecklist from "react-password-checklist";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, Loader2, MailCheck, X } from "lucide-react";

import { OTPInput, type OTPStatus } from "@/components/motion/otp-input";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/brand/brand";
import { useCustomerLogo } from "@/hooks/useCustomerLogo";
import { SPRING_SWAP } from "@/lib/ease";
import { safeRedirectTarget } from "../utilities/loginRedirect";
import { ApplicationError, UnauthorizedError } from "../general/Errors";
import { useModalContext } from "../components/ModalProvider";
import { validateMFA } from '../UserAPI';
import PhoneInput from '../components/PhoneInput';
import { jwtAuthService, getAuthenticationFromToken } from '../JWTAuthService';
import { useCustomerTheme } from '../Theme';
import { clearSsoSessionNotice, readSsoSessionNotice, startEntraSignIn } from '../utilities/entraSso';
import { cameFromSibling, markAuthEntrance, markSiblingNavigation } from './auth/authEntrance';
import {
  AuthBackLink,
  AuthError,
  AutoHeight,
  LEAVE_MS,
  AuthField,
  AuthFootnote,
  AuthHeader,
  AuthShell,
  AuthStep,
  AuthSubmit,
  MicrosoftMark,
  OrDivider,
  Reveal,
  SegmentedChoice,
  SuccessCheck,
} from './auth/AuthLayout';

enum OTPState {
  pristine = 'pristine',
  validating = 'validating',
  incorrect = 'incorrect',
  correct = 'correct',
}

/** Screens of the sign-in flow; `signin` covers both the email and the password phase. */
type LoginStep = 'signin' | 'forgot' | 'credentials' | 'mfa';

/** How long the check stays in place of the code field before the page fades out. */
const VERIFIED_HOLD_MS = 1100;

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { pushError } = useModalContext() as any;
  const location = useLocation();
  const [enterFromSibling] = useState(cameFromSibling);
  const { search } = location;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const redirectTo = searchParams.get("redirect");
  const safeRedirect = safeRedirectTarget(redirectTo);
  const tenantParam = searchParams.get("tenant");
  const { themeLoading } = useCustomerTheme();
  const hasTriggeredPostLoginNavigation = useRef(false);
  const fieldId = useId();
  const usernameFieldId = `${fieldId}-username`;
  const passwordFieldId = `${fieldId}-password`;
  const resetEmailFieldId = `${fieldId}-reset-email`;
  const newPasswordFieldId = `${fieldId}-new-password`;
  const newPasswordRepeatFieldId = `${fieldId}-new-password-repeat`;
  const passwordInputRef = useRef<HTMLInputElement>(null);
  // When ?tenant=<id> is present, CustomerThemeProvider fetches that tenant's
  // theme (including logoURL). useCustomerLogo prefers that logo, so a branded
  // entry point ("/login?tenant=acme") shows the tenant's logo and falls back
  // to the The-Real-Insight logo.
  const { logo: brandOrTenantLogo, isFallback: isFallbackLogo } = useCustomerLogo();
  // A brand that floods the page with its own color (FireIQ) shows the column
  // on a light card, so it keeps its regular wordmark here.
  const logoSrc = brandOrTenantLogo;
  const withTenant = (path: string) =>
    tenantParam && tenantParam !== 'null'
      ? `${path}${path.includes('?') ? '&' : '?'}tenant=${encodeURIComponent(tenantParam)}`
      : path;
  const navigateToPostLogin = (replace = false) => {
    hasTriggeredPostLoginNavigation.current = true;
    // A visible sign-in hands over to the shell's entrance; the silent
    // redirect of an existing session (replace) stays instant.
    if (!replace) markAuthEntrance();
    navigate(safeRedirect || withTenant('/chatbot'), replace ? { replace: true } : undefined);
  };
  const [username, setUsername] = useState(searchParams.get("email") || '');
  const [oldPassword, setOldPassword] = useState(searchParams.get("password") || '');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState(undefined);
  const [passwordValid, setPasswordValid] = useState(false);
  const [phoneValid, setPhoneValid] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [authentication, setAuthentication] = useAtom(authenticationAtom) as any;
  const [, setJwtTokens] = useAtom(jwtTokensAtom);
  const [method, setMethod] = useState();
  // The user's organization signs in with Microsoft only (checkUser `ssoOnly`): no password field.
  const [ssoOnly, setSsoOnly] = useState(false);
  // Why the last session ended, when the server ended it for SSO only — shown once.
  const [ssoNotice] = useState(readSsoSessionNotice);
  useEffect(clearSsoSessionNotice, []);
  const [user, setUser] = useState() as any;
  const [otpState, setOTPState] = useState(OTPState.pristine);
  const [firstOTPSent, setFirstOTPSent] = useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = useState() as any;
  const [passwordErrorMessage, setPasswordErrorMessage] = useState() as any;
  const [passwordRetries, setPasswordRetries] = useState(0);
  const [authenticatorAppEnabled, setAuthenticatorAppEnabled] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<'sms' | 'authenticator'>('authenticator');
  // The password reset lives on its own screen of this page instead of two
  // stacked dialogs; it starts with the email typed so far.
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | undefined>();
  // Per-button async-action flags. Each button toggles its own flag so it can
  // render the spinner / disable itself during the round-trip.
  const [submittingForm, setSubmittingForm] = useState(false);
  const [submittingChange, setSubmittingChange] = useState(false);
  const [submittingOTP, setSubmittingOTP] = useState(false);
  const [submittingReset, setSubmittingReset] = useState(false);
  // Set once the code is accepted: a check takes the code field's place, then
  // the page fades out (`leaving`) and the redirect follows — as in the app.
  const [verified, setVerified] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const verifiedTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => verifiedTimers.current.forEach(clearTimeout), []);

  const step: LoginStep = user
    ? (user.passwordStatus === 'change' ? 'credentials' : 'mfa')
    : (showForgot ? 'forgot' : 'signin');
  // Going back to the sign-in screen slides in from the left.
  const stepDirection: 1 | -1 = step === 'signin' ? -1 : 1;

  // Set mfaMethod to 'sms' if authenticatorAppEnabled is false
  useEffect(() => {
    if (authenticatorAppEnabled) {
      setMfaMethod('authenticator');
    } else {
      setMfaMethod('sms');
    }
  }, [authenticatorAppEnabled]);

  // If the user is already authenticated, skip the login form and go straight
  // to the post-login target. Handles the case where someone revisits a
  // branded entry point like /login?tenant=<id> with an existing session.
  useEffect(() => {
    // During password-change or MFA challenge, keep the user on this page
    // even if a token-backed authentication object already exists.
    if (!hasTriggeredPostLoginNavigation.current && authentication?.customer && !user) {
      navigateToPostLogin(true);
    }
    // withTenant is a stable pure function of tenantParam; depending on
    // authentication + tenantParam is sufficient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer, user, tenantParam, safeRedirect]);

  // The password field slides in after the email check; focus it once it is there.
  useEffect(() => {
    if (method && step === 'signin') {
      const timer = setTimeout(() => passwordInputRef.current?.focus(), reduce ? 0 : 120);
      return () => clearTimeout(timer);
    }
  }, [method, step, reduce]);

  // Das Codefeld zeigt Fehler (Rütteln, roter Rahmen, Meldung) und Erfolg (Haken)
  // selbst an; hier bleibt nur das Warten auf die Prüfung.
  const otpInputStatus: OTPStatus =
    otpState === OTPState.incorrect ? 'error'
      : otpState === OTPState.correct ? 'success'
        : 'idle';

  const storeVerifiedTokens = (result: any) => {
    setJwtTokens({ accessToken: result.jwtToken, refreshToken: result.refreshToken || '' });
    jwtAuthService.setTokens({
      accessToken: result.jwtToken,
      refreshToken: result.refreshToken || ''
    });
  };

  const verifyCode = async (otp: string) => {
    if (!otp || otp.length < 6) {
      setOTPState(OTPState.pristine);
      return;
    }
    setOTPState(OTPState.validating);
    try {
      const result = mfaMethod === 'authenticator'
        ? await validateMFA(user.email, otp)
        : await userAPI.checkOTP(user.email, otp);
      if (result.success && result.jwtToken) {
        setOTPState(OTPState.correct);
        storeVerifiedTokens(result);
        setVerified(true);
        verifiedTimers.current.push(
          setTimeout(() => setLeaving(true), reduce ? 0 : VERIFIED_HOLD_MS),
          setTimeout(() => navigateToPostLogin(), reduce ? 300 : VERIFIED_HOLD_MS + LEAVE_MS),
        );
      } else {
        if (result.success) console.error('Code verification successful but no token received');
        setOTPState(OTPState.incorrect);
      }
    } catch (error: any) {
      setOTPState(OTPState.incorrect);
    }
  };

  const resetSignIn = () => {
    setMethod(undefined);
    setSsoOnly(false);
    setOldPassword('');
    setPasswordErrorMessage(undefined);
  };

  const openForgot = () => {
    setResetEmail(username);
    setResetSent(false);
    setResetError(undefined);
    setShowForgot(true);
  };

  // Back to sign-in with the email from the reset form; the email check runs again.
  const backFromForgot = () => {
    if (resetEmail.trim()) setUsername(resetEmail.trim());
    resetSignIn();
    setShowForgot(false);
  };

  const submitSignIn = async (event: React.FormEvent) => {
    event.preventDefault();

    if (submittingForm) return;
    setSubmittingForm(true);
    try {
      if (method && ssoOnly) {
        startEntraSignIn(username.trim(), safeRedirect);
      } else if (method) {
        try {
          setPasswordErrorMessage(undefined);
          // @ts-ignore
          let user = await userAPI.login(username, oldPassword);
          if (!user.passwordStatus) user.passwordStatus = 'permanent';

          // Check authentication requirements from new JWT protocol
          if (user.passwordChangeRequired || user.mfaRequired) {
            setPassword('');
            setPasswordRepeat(undefined);
            setPasswordValid(false);
            setPhoneValid(false);
            setMobileNumber('');
            setUser(user);
            // JWT tokens are automatically stored by the login function in UserAPI
            // Set authentication atom from JWT token
            const authentication = getAuthenticationFromToken();
            if (authentication) {
              setAuthentication(authentication);
            }
          } else {
            // User is fully authenticated, proceed to main application
            setAuthentication(user as any);
            // Store JWT tokens if available
            if (user.accessToken) {
              setJwtTokens({ accessToken: user.accessToken, refreshToken: user.refreshToken || '' });
            }
            // Navigate to redirect target if provided
            navigateToPostLogin();
          }
        } catch (error: any) {
          if (error instanceof ApplicationError) {
            setPasswordErrorMessage((t((error as ApplicationError).message)));
          } else if (error instanceof UnauthorizedError) {
            setPasswordErrorMessage((t('loginPage.invalidPassword')));

            if (passwordRetries >= 3) {
              setUsername('');
              setPasswordRetries(0);
              resetSignIn();
            } else {
              setPasswordRetries(passwordRetries + 1);
            }
          } else {
            pushError(error);
          }
        }
      } else {
        try {
          setUsernameErrorMessage(undefined);

          // @ts-ignore
          const check = await userAPI.checkUser(username.trim());
          if (username !== username.trim()) setUsername(username.trim());

          setMethod(check.method || 'internal');
          setSsoOnly(Boolean(check.ssoOnly));
          setAuthenticatorAppEnabled(check.authenticatorAppEnabled);
        } catch (error: any) {
          if (error instanceof ApplicationError) {
            setUsernameErrorMessage((t((error as ApplicationError).message)));
          } else if (error instanceof UnauthorizedError) {
            setUsernameErrorMessage((t('loginPage.invalidUser')));
          } else {
            pushError(error);
          }
        }
      }
    } finally {
      setSubmittingForm(false);
    }
  };

  const submitReset = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingReset || !resetEmail.trim()) return;
    setSubmittingReset(true);
    setResetError(undefined);
    try {
      await userAPI.resetPassword(resetEmail.trim());
      setResetSent(true);
    } catch (error: any) {
      setResetError(error?.message ? t(error.message) : String(error));
    } finally {
      setSubmittingReset(false);
    }
  };

  const submitCredentials = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingChange || !passwordValid || !phoneValid) return;
    setSubmittingChange(true);
    setPasswordErrorMessage(undefined);
    try {
      // @ts-ignore
      const updatedUser = await userAPI.updateUserCredentials(user.customer, user._id, oldPassword, password, mobileNumber);
      const typedUpdatedUser = updatedUser as any;

      // Check if the server returned new JWT tokens (indicating successful password change)
      if (typedUpdatedUser.accessToken && typedUpdatedUser.refreshToken) {
        setJwtTokens({ accessToken: typedUpdatedUser.accessToken, refreshToken: typedUpdatedUser.refreshToken });

        if (typedUpdatedUser.passwordChangeRequired === false && !typedUpdatedUser.mfaRequired) {
          // User is fully authenticated, proceed to main application
          setAuthentication(typedUpdatedUser as any);
          navigateToPostLogin();
        } else {
          // MFA comes next — the server sends the code after the password change.
          setUser(typedUpdatedUser);
        }
      } else {
        setUser(typedUpdatedUser);
      }
    } catch (error: any) {
      if (error instanceof ApplicationError) {
        setPasswordErrorMessage((t((error as ApplicationError).message)));
      } else if (error.status === 401 && (error.passwordChangeRequired || error.mfaRequired)) {
        setPasswordErrorMessage(t(error.passwordChangeRequired ? 'loginPage.passwordChangeRequired' : 'loginPage.mfaRequired'));
      } else {
        pushError(error);
      }
    } finally {
      setSubmittingChange(false);
    }
  };

  const sendCode = async () => {
    if (submittingOTP) return;
    setSubmittingOTP(true);
    try {
      await userAPI.sendOTP();
      setFirstOTPSent(true);
    } catch (error) {
      pushError(error);
    } finally {
      setSubmittingOTP(false);
    }
  };

  const renderStep = () => {
    switch (step) {

      case 'signin':
        return <>
          <AuthHeader
            title={t('loginPage.signInTitle')}
            subtitle={
              <>
                {t('loginPage.noAccount')}{' '}
                <button
                  type="button"
                  onClick={() => { markSiblingNavigation(); navigate(withTenant('/signup')); }}
                  className="rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('loginPage.signUp')}
                </button>
              </>
            }
          />
          {BRAND.entraSsoEnabled && (
            <div className="mb-5 flex flex-col gap-5">
              <Button
                type="button"
                variant="outline"
                className="h-10 w-full gap-2"
                onClick={() => startEntraSignIn(username.trim(), safeRedirect)}
              >
                <MicrosoftMark />
                {t('entraSso.signInWithMicrosoft')}
              </Button>
              <OrDivider label={t('loginPage.or')} />
            </div>
          )}
          {ssoNotice && (
            <p role="status" className="mb-5 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              {t('entraSso.sessionEnded')}
            </p>
          )}
          <form className="flex flex-col" onSubmit={submitSignIn} noValidate>
            <AuthField
              id={usernameFieldId}
              label={t('loginPage.email')}
              type="email"
              inputMode="email"
              autoComplete="username"
              autoFocus={!username}
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t('loginPage.emailPlaceholder')}
              readOnly={!!method}
              className={method ? "h-10 cursor-default bg-muted/50 text-muted-foreground" : "h-10"}
              value={username}
              error={usernameErrorMessage}
              labelAction={method ? (
                <button
                  type="button"
                  onClick={resetSignIn}
                  className="rounded-sm text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('loginPage.change')}
                </button>
              ) : undefined}
              onChange={(event) => {
                setUsername(event.target.value);
                if (usernameErrorMessage) setUsernameErrorMessage(undefined);
              }}
            />
            <Reveal show={!!method && ssoOnly}>
              <p className="pt-4 text-sm text-muted-foreground">{t('entraSso.ssoOnlyHint')}</p>
            </Reveal>
            <Reveal show={!!method && !ssoOnly}>
              <div className="pt-4">
                <AuthField
                  id={passwordFieldId}
                  ref={passwordInputRef}
                  label={t('loginPage.password')}
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={oldPassword}
                  error={passwordErrorMessage}
                  onChange={(event) => {
                    setOldPassword(event.target.value);
                  }}
                />
              </div>
            </Reveal>
            <AuthSubmit
              className="mt-6"
              busy={submittingForm}
              disabled={method ? (!username || (!ssoOnly && !oldPassword)) : !username.trim()}
            >
              {ssoOnly ? t('entraSso.signInWithMicrosoft') : method ? t('loginPage.signIn') : t('loginPage.continue')}
            </AuthSubmit>
          </form>
          {!ssoOnly && (
            <AuthFootnote
              className="mt-6"
              text={t('loginPage.forgottenPassword')}
              action={t('loginPage.resetPassword')}
              onAction={openForgot}
            />
          )}
        </>;

      case 'forgot':
        return resetSent
          ? <>
            <motion.div
              className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-success/10 text-success"
              initial={reduce ? false : { scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={SPRING_SWAP}
            >
              <MailCheck aria-hidden="true" className="h-5 w-5" />
            </motion.div>
            <AuthHeader
              title={t('loginPage.resetSentTitle')}
              subtitle={t('loginPage.resetPasswordSuccess', { email: resetEmail.trim() })}
            />
            <Button
              type="button"
              className="h-10 w-full"
              onClick={backFromForgot}
            >
              {t('loginPage.backToLogin')}
            </Button>
          </>
          : <>
            <AuthHeader
              title={t('loginPage.resetTitle')}
              subtitle={t('loginPage.resetSubtitle')}
            />
            <form className="flex flex-col" onSubmit={submitReset} noValidate>
              <AuthField
                id={resetEmailFieldId}
                label={t('loginPage.email')}
                type="email"
                inputMode="email"
                autoComplete="username"
                autoFocus
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                placeholder={t('loginPage.emailPlaceholder')}
                value={resetEmail}
                error={resetError}
                onChange={(event) => setResetEmail(event.target.value)}
              />
              <AuthSubmit className="mt-6" busy={submittingReset} disabled={!resetEmail.trim()}>
                {t('loginPage.sendResetLink')}
              </AuthSubmit>
            </form>
            <AuthFootnote
              className="mt-6"
              text={t('loginPage.rememberPassword')}
              action={t('loginPage.backToLogin')}
              onAction={backFromForgot}
            />
          </>;

      case 'credentials':
        return <>
          <AuthHeader title={t('loginPage.credentialUpdate')} subtitle={t('loginPage.credentialUpdateSubtitle')} />
          <form className="flex flex-col gap-4" onSubmit={submitCredentials} noValidate>
            <AuthField
              id={newPasswordFieldId}
              label={t('loginPage.newPassword')}
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <AuthField
              id={newPasswordRepeatFieldId}
              label={t('loginPage.newPasswordRepeat')}
              type="password"
              autoComplete="new-password"
              value={passwordRepeat ?? ''}
              onChange={(event) => setPasswordRepeat(event.target.value as any)}
            />
            <PasswordChecklist
              rules={["minLength", "specialChar", "number", "capital", "match"]}
              minLength={5}
              value={password || ''}
              valueAgain={passwordRepeat}
              className="text-sm"
              itemClassName="text-sm"
              validTextColor="hsl(var(--foreground))"
              invalidTextColor="hsl(var(--muted-foreground))"
              iconComponents={{
                ValidIcon: <Check aria-hidden="true" className="mr-1.5 h-[18px] w-[18px] shrink-0 text-success" />,
                InvalidIcon: <X aria-hidden="true" className="mr-1.5 h-[18px] w-[18px] shrink-0 text-destructive" />,
              }}
              messages={{
                minLength: t('loginPage.passwordRuleMinLength'),
                specialChar: t('loginPage.passwordRuleSpecialChar'),
                number: t('loginPage.passwordRuleNumber'),
                capital: t('loginPage.passwordRuleCapital'),
                match: t('loginPage.passwordRuleMatch'),
              }}
              onChange={(valid: boolean) => setPasswordValid(valid)}
            />
            {/* PhoneInput ist noch nicht migriert und trägt feste
                Mindestbreiten — der Scrollrahmen hält den Überlauf im
                Feld statt in der Seite. */}
            <div className="min-w-0 overflow-x-auto">
              <PhoneInput
                label={t('loginPage.mobileNumber')}
                value={mobileNumber}
                onChange={(value: string) => setMobileNumber(value)}
                onValidationChange={(valid: boolean) => setPhoneValid(valid)}
                required
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('loginPage.mobileNumberExplanation')}
            </p>
            <AuthError message={passwordErrorMessage} />
            <AuthSubmit className="mt-2" busy={submittingChange} disabled={!passwordValid || !phoneValid}>
              {t('loginPage.saveAndContinue')}
            </AuthSubmit>
          </form>
        </>;

      case 'mfa':
        return <>
          <AuthHeader
            title={t('loginPage.accountVerification')}
            subtitle={mfaMethod === 'authenticator'
              ? t('loginPage.enterAuthenticatorCode')
              : t('loginPage.weSendAnSMS')}
            hidden={verified}
          />
          <div className="flex flex-col">
            <Reveal show={authenticatorAppEnabled && !verified}>
              <div className="pb-5">
                <SegmentedChoice
                  label={t('loginPage.accountVerification')}
                  value={mfaMethod}
                  onChange={(value) => {
                    setMfaMethod(value);
                    setOTPState(OTPState.pristine);
                  }}
                  options={[
                    { value: 'authenticator', label: t('loginPage.authenticatorTab') },
                    { value: 'sms', label: t('loginPage.smsTab') },
                  ]}
                />
              </div>
            </Reveal>
            <AutoHeight>
              <AnimatePresence mode="wait" initial={false}>
                {verified ? (
                  <motion.div
                    key="verified"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                  >
                    <SuccessCheck label={t('loginPage.verifiedTitle')} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="code"
                    className="flex flex-col gap-2"
                    exit={reduce ? undefined : { opacity: 0, scale: 0.96, filter: 'blur(4px)' }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                  >
                    <OTPInput
                      key={mfaMethod}
                      autoFocus
                      aria-label={t('settingsPanel.securityPanel.enterCode')}
                      status={otpInputStatus}
                      disabled={otpState === OTPState.validating}
                      errorMessage={t('errors.invalidValidationCode')}
                      fullWidth
                      onChange={verifyCode}
                    />
                    <div role="status" className="flex h-5 items-center gap-2 text-sm text-muted-foreground">
                      {otpState === OTPState.validating && <>
                        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                        {t('loginPage.verifying')}
                      </>}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </AutoHeight>
            <Reveal show={!verified}>
              <div className="flex flex-col gap-5 pt-5">
                {mfaMethod === 'sms' && (
                  <Button
                    type="button"
                    variant={firstOTPSent ? 'outline' : 'default'}
                    className="h-10 w-full"
                    disabled={submittingOTP}
                    onClick={sendCode}
                  >
                    {submittingOTP && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                    {firstOTPSent ? t('loginPage.sendAgain') : t('loginPage.send')}
                  </Button>
                )}
                <p className="text-sm text-muted-foreground">
                  {t('loginPage.lostMFAMethod')}{' '}
                  <span className="font-medium text-foreground">{t('loginPage.contactUs')}</span>
                </p>
              </div>
            </Reveal>
          </div>
        </>;
    }
  };

  return (
    <AuthShell
      logoSrc={logoSrc}
      logoAlt={isFallbackLogo ? BRAND.name : ''}
      // When ?tenant=<id> is in play, wait while the tenant theme loads to
      // avoid a brief default-logo flash.
      showLogo={!tenantParam || tenantParam === 'null' || !themeLoading}
      leaving={leaving}
      logoHidden={verified}
      headerAction={step === 'forgot'
        ? <AuthBackLink label={t('loginPage.backToLogin')} onClick={backFromForgot} />
        : undefined}
    >
      <AuthStep
        stepKey={step + (step === 'forgot' && resetSent ? '-sent' : '')}
        direction={stepDirection}
        enterOnMount={enterFromSibling}
      >
        {renderStep()}
      </AuthStep>
    </AuthShell>
  );
};
